import React from 'react';

const ResumePreview = ({ formData }) => {
  // Helper function to check if data exists
  const hasContent = (value) => {
    if (!value) return false;
    if (Array.isArray(value)) return value.length > 0;
    if (typeof value === 'string') return value.trim().length > 0;
    return true;
  };

  // Helper to check if section has valid data
  const hasValidSkills = (skills) => {
    if (!skills || skills.length === 0) return false;
    return skills.some(skill => 
      skill.title?.trim().length > 0 || 
      (skill.items && skill.items.some(item => item?.trim().length > 0))
    );
  };

  const hasValidExperiences = (experiences) => {
    if (!experiences || experiences.length === 0) return false;
    return experiences.some(exp => 
      exp.designation?.trim().length > 0 || 
      exp.company?.trim().length > 0
    );
  };

  const hasValidProjects = (projects) => {
    if (!projects || projects.length === 0) return false;
    return projects.some(project => 
      project.name?.trim().length > 0 || 
      (project.summary && project.summary.some(item => item?.trim().length > 0))
    );
  };

  const hasValidEducation = (education) => {
    if (!education || education.length === 0) return false;
    return education.some(edu => 
      edu.name?.trim().length > 0 || 
      edu.college?.trim().length > 0
    );
  };

  const hasValidCertifications = (certifications) => {
    if (!certifications || certifications.length === 0) return false;
    return certifications.some(cert => 
      cert.name?.trim().length > 0 || 
      cert.institution?.trim().length > 0
    );
  };

  return (
    <div className="resume-preview">
      {/* Header Section */}
      <div className="resume-header">
        <h1 className="resume-name">{formData.name || 'Your Name'}</h1>
        
        <div className="resume-contact">
          {hasContent(formData.mobile) && (
            <span className="contact-item">
              📱 {formData.mobile}
            </span>
          )}
          {hasContent(formData.email) && (
            <span className="contact-item">
              ✉️ {formData.email}
            </span>
          )}
          {hasContent(formData.address) && (
            <span className="contact-item">
              📍 {formData.address}
            </span>
          )}
        </div>

        {(hasContent(formData.link1) || hasContent(formData.link2)) && (
          <div className="resume-links">
            {hasContent(formData.link1) && (
              <a href={formData.link1} target="_blank" rel="noopener noreferrer" className="link-item">
                🔗 LinkedIn
              </a>
            )}
            {hasContent(formData.link2) && (
              <a href={formData.link2} target="_blank" rel="noopener noreferrer" className="link-item">
                🔗 Portfolio
              </a>
            )}
          </div>
        )}
      </div>

      {/* Objective/Summary Section */}
      {hasContent(formData.objective) && (
        <div className="resume-section">
          <h2 className="section-title">Professional Summary</h2>
          <div className="section-content">
            <p className="objective-text">{formData.objective}</p>
          </div>
        </div>
      )}

      {/* Skills Section */}
      {hasValidSkills(formData.skills) && (
        <div className="resume-section">
          <h2 className="section-title">Skills</h2>
          <div className="section-content">
            {formData.skills.map((skill, index) => (
              (skill.title?.trim().length > 0 || (skill.items && skill.items.some(item => item?.trim().length > 0))) && (
                <div key={index} className="skill-group">
                  {skill.title && skill.title.trim().length > 0 && (
                    <h3 className="skill-category">{skill.title}:</h3>
                  )}
                  <div className="skill-items">
                    {skill.items && skill.items.map((item, idx) => (
                      item && item.trim().length > 0 && (
                        <span key={idx} className="skill-tag">{item}</span>
                      )
                    ))}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* Experience Section */}
      {hasValidExperiences(formData.experiences) && (
        <div className="resume-section">
          <h2 className="section-title">Professional Experience</h2>
          <div className="section-content">
            {formData.experiences.map((experience, index) => (
              (experience.designation?.trim().length > 0 || experience.company?.trim().length > 0) && (
                <div key={index} className="experience-item">
                  <div className="experience-header">
                    <h3 className="experience-title">
                      {experience.designation && experience.designation.trim().length > 0 && experience.designation}
                      {experience.designation && experience.company && experience.designation.trim().length > 0 && experience.company.trim().length > 0 && ' - '}
                      {experience.company && experience.company.trim().length > 0 && (
                        <span className="company-name">{experience.company}</span>
                      )}
                    </h3>
                    {(experience.startDate?.trim().length > 0 || experience.endDate?.trim().length > 0) && (
                      <span className="experience-date">
                        {experience.startDate && experience.startDate.trim().length > 0 && experience.startDate}
                        {experience.startDate && experience.endDate && experience.startDate.trim().length > 0 && experience.endDate.trim().length > 0 && ' - '}
                        {experience.endDate && experience.endDate.trim().length > 0 && experience.endDate}
                      </span>
                    )}
                  </div>
                  {experience.workSummary && experience.workSummary.length > 0 && (
                    <ul className="experience-points">
                      {experience.workSummary.map((point, idx) => (
                        point && point.trim().length > 0 && (
                          <li key={idx}>{point}</li>
                        )
                      ))}
                    </ul>
                  )}
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* Projects Section */}
      {hasValidProjects(formData.projects) && (
        <div className="resume-section">
          <h2 className="section-title">Projects</h2>
          <div className="section-content">
            {formData.projects.map((project, index) => (
              (project.name?.trim().length > 0 || (project.summary && project.summary.some(s => s?.trim().length > 0))) && (
                <div key={index} className="project-item">
                  {project.name && project.name.trim().length > 0 && (
                    <h3 className="project-title">{project.name}</h3>
                  )}
                  {project.summary && project.summary.length > 0 && (
                    <ul className="project-points">
                      {project.summary.map((point, idx) => (
                        point && point.trim().length > 0 && (
                          <li key={idx}>{point}</li>
                        )
                      ))}
                    </ul>
                  )}
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* Education Section */}
      {hasValidEducation(formData.education) && (
        <div className="resume-section">
          <h2 className="section-title">Education</h2>
          <div className="section-content">
            {formData.education.map((edu, index) => (
              (edu.name?.trim().length > 0 || edu.college?.trim().length > 0) && (
                <div key={index} className="education-item">
                  <div className="education-header">
                    <h3 className="education-title">
                      {edu.name && edu.name.trim().length > 0 && edu.name}
                      {edu.name && edu.college && edu.name.trim().length > 0 && edu.college.trim().length > 0 && ' - '}
                      {edu.college && edu.college.trim().length > 0 && (
                        <span className="college-name">{edu.college}</span>
                      )}
                    </h3>
                    {(edu.startDate?.trim().length > 0 || edu.endDate?.trim().length > 0) && (
                      <span className="education-date">
                        {edu.startDate && edu.startDate.trim().length > 0 && edu.startDate}
                        {edu.startDate && edu.endDate && edu.startDate.trim().length > 0 && edu.endDate.trim().length > 0 && ' - '}
                        {edu.endDate && edu.endDate.trim().length > 0 && edu.endDate}
                      </span>
                    )}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* Certifications Section */}
      {hasValidCertifications(formData.certifications) && (
        <div className="resume-section">
          <h2 className="section-title">Certifications</h2>
          <div className="section-content">
            {formData.certifications.map((certification, index) => (
              (certification.name?.trim().length > 0 || certification.institution?.trim().length > 0) && (
                <div key={index} className="certification-item">
                  <div className="certification-header">
                    <h3 className="certification-title">
                      {certification.name && certification.name.trim().length > 0 && certification.name}
                      {certification.name && certification.institution && certification.name.trim().length > 0 && certification.institution.trim().length > 0 && ' - '}
                      {certification.institution && certification.institution.trim().length > 0 && (
                        <span className="institution-name">{certification.institution}</span>
                      )}
                    </h3>
                    {(certification.startDate?.trim().length > 0 || certification.endDate?.trim().length > 0) && (
                      <span className="certification-date">
                        {certification.startDate && certification.startDate.trim().length > 0 && certification.startDate}
                        {certification.startDate && certification.endDate && certification.startDate.trim().length > 0 && certification.endDate.trim().length > 0 && ' - '}
                        {certification.endDate && certification.endDate.trim().length > 0 && certification.endDate}
                      </span>
                    )}
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      )}

      {/* Empty State */}
      {!formData.name && !hasContent(formData.objective) && !hasValidSkills(formData.skills) && 
       !hasValidExperiences(formData.experiences) && !hasValidProjects(formData.projects) && 
       !hasValidEducation(formData.education) && !hasValidCertifications(formData.certifications) && (
        <div className="empty-state">
          <div className="empty-icon">📄</div>
          <h3>Start Building Your Resume</h3>
          <p>Fill in the form sections to see your resume preview here</p>
        </div>
      )}
    </div>
  );
};

export default ResumePreview;